import React, { useState, useEffect } from 'react';
import { axiosGet } from '../../../utils/axiosGet.js';
import { devError } from '../../../utils/devLog';

const CronJobMonitorTable = () => {
  const [cronLogs, setCronLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchCronLogs();
    // Refresh every 60 seconds
    const interval = setInterval(fetchCronLogs, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchCronLogs = async () => {
    try {
      setLoading(true);
      const response = await axiosGet(
        'cron-job-logs?sort=startedAt:desc&pagination[limit]=20',
      );
      setCronLogs(response.data.data);
      setError(null);
    } catch (err) {
      devError('Error fetching cron logs:', err);
      setError('Failed to load cron job logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: 'success',
      failed: 'danger',
      running: 'warning',
    };
    return badges[status] || 'secondary';
  };

  const getJobDisplayName = (jobName) => {
    const names = {
      scheduleSFCronJob: 'Salesforce Sync',
      sendRefereeEmails: 'Referee Email Reminders',
      archiveNominations: 'Archive Nominations',
      generateBulkPdfs: 'Generate Bulk PDFs',
      createNominationPdfsZip: 'Create PDF Zip',
      processRoundEnd: 'Process Round End',
    };
    return names[jobName] || jobName;
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setShowDetailsModal(true);
  };

  const closeModal = () => {
    setShowDetailsModal(false);
    setSelectedLog(null);
  };

  const getSuccessRate = (log) => {
    const { recordsProcessed, recordsSucceeded } = log.attributes;
    if (!recordsProcessed || recordsProcessed === 0) return 'N/A';
    const rate = ((recordsSucceeded / recordsProcessed) * 100).toFixed(1);
    return `${rate}%`;
  };

  const renderContent = () => {
    if (loading && cronLogs.length === 0) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading cron job logs...</p>
        </div>
      );
    }

    if (cronLogs.length === 0) {
      return (
        <div className="alert alert-info" role="alert">
          <i className="fa fa-info-circle me-2" />
          No cron job logs found.
        </div>
      );
    }

    return (
      <div className="table-responsive">
        <table className="table table-hover table-striped">
          <thead className="thead-dark">
            <tr>
              <th>Job Name</th>
              <th>Status</th>
              <th>Started At</th>
              <th>Duration</th>
              <th className="text-center">Processed</th>
              <th className="text-center">Succeeded</th>
              <th className="text-center">Failed</th>
              <th className="text-center">Success Rate</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cronLogs.map((log) => (
              <tr key={log.id}>
                <td>
                  <strong>{getJobDisplayName(log.attributes.jobName)}</strong>
                  <br />
                  <small className="text-muted">{log.attributes.jobName}</small>
                </td>
                <td>
                  <span
                    className={`badge badge-${getStatusBadge(log.attributes.status)}`}
                  >
                    {log.attributes.status === 'running' && (
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                      />
                    )}
                    {log.attributes.status}
                  </span>
                </td>
                <td>
                  <small>{formatDate(log.attributes.startedAt)}</small>
                </td>
                <td>
                  <span className="badge badge-secondary">
                    {formatDuration(log.attributes.duration)}
                  </span>
                </td>
                <td className="text-center">
                  <strong>{log.attributes.recordsProcessed || 0}</strong>
                </td>
                <td className="text-center text-success">
                  <strong>{log.attributes.recordsSucceeded || 0}</strong>
                </td>
                <td className="text-center text-danger">
                  <strong>{log.attributes.recordsFailed || 0}</strong>
                </td>
                <td className="text-center">
                  <span
                    className={`badge badge-${
                      log.attributes.recordsFailed > 0 ? 'warning' : 'success'
                    }`}
                  >
                    {getSuccessRate(log)}
                  </span>
                </td>
                <td className="text-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-info"
                    onClick={() => handleViewDetails(log)}
                    title="View Details"
                  >
                    <i className="fa fa-eye" />
                    <span className="visually-hidden">View Details</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div className="col-md-12 mb-3">
        <div className="card admin-card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Cron Job Monitor</h4>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={fetchCronLogs}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Refreshing...
                </>
              ) : (
                <>
                  <i className="fa fa-refresh me-2" />
                  Refresh
                </>
              )}
            </button>
          </div>
          <div className="card-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                <i className="fa fa-exclamation-triangle me-2" />
                {error}
              </div>
            )}

            {renderContent()}

            {cronLogs.length > 0 && (
              <div className="mt-3">
                <small className="text-muted">
                  <i className="fa fa-info-circle me-1" />
                  Showing last 20 executions. Auto-refreshes every 60 seconds.
                </small>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedLog && (
        <>
          <div className="modal-backdrop fade show" />

          <div className="modal fade show d-block" tabIndex="-1" role="dialog">
            <div
              className="modal-dialog modal-lg modal-dialog-centered"
              role="document"
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Cron Job Details:{' '}
                    {getJobDisplayName(selectedLog.attributes.jobName)}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong>Job Name:</strong>
                      <p className="mb-0">{selectedLog.attributes.jobName}</p>
                    </div>
                    <div className="col-md-6">
                      <strong>Status:</strong>
                      <p className="mb-0">
                        <span
                          className={`badge badge-${getStatusBadge(selectedLog.attributes.status)}`}
                        >
                          {selectedLog.attributes.status}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong>Started At:</strong>
                      <p className="mb-0">
                        {formatDate(selectedLog.attributes.startedAt)}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <strong>Completed At:</strong>
                      <p className="mb-0">
                        {formatDate(selectedLog.attributes.completedAt)}
                      </p>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-3">
                      <strong>Duration:</strong>
                      <p className="mb-0">
                        {formatDuration(selectedLog.attributes.duration)}
                      </p>
                    </div>
                    <div className="col-md-3">
                      <strong>Processed:</strong>
                      <p className="mb-0">
                        {selectedLog.attributes.recordsProcessed || 0}
                      </p>
                    </div>
                    <div className="col-md-3">
                      <strong>Succeeded:</strong>
                      <p className="mb-0 text-success">
                        {selectedLog.attributes.recordsSucceeded || 0}
                      </p>
                    </div>
                    <div className="col-md-3">
                      <strong>Failed:</strong>
                      <p className="mb-0 text-danger">
                        {selectedLog.attributes.recordsFailed || 0}
                      </p>
                    </div>
                  </div>

                  {selectedLog.attributes.errorMessage && (
                    <div className="mb-3">
                      <strong>Error Message:</strong>
                      <div className="alert alert-danger mt-2">
                        <pre
                          className="mb-0"
                          style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {selectedLog.attributes.errorMessage}
                        </pre>
                      </div>
                    </div>
                  )}

                  {selectedLog.attributes.metadata && (
                    <div className="mb-3">
                      <strong>Metadata:</strong>
                      <div className="alert alert-info mt-2">
                        <pre
                          className="mb-0"
                          style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            maxHeight: '300px',
                            overflow: 'auto',
                          }}
                        >
                          {JSON.stringify(
                            selectedLog.attributes.metadata,
                            null,
                            2,
                          )}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CronJobMonitorTable;
